import { SeoHead } from "@/components/SeoHead";
import { PageTransition } from "@/components/layout/PageTransition";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Handshake, FileText, Scale, TrendingUp, Megaphone, Shield, Trophy, ShieldCheck, LucideIcon } from "lucide-react";
import { useCms } from "@/hooks/useCms";

const ICON_MAP: Record<string, LucideIcon> = {
  FileText, TrendingUp, Scale, Megaphone, Shield, Handshake, Trophy, ShieldCheck,
};

const DEFAULT_FEATURES = [
  { icon: "FileText", title: "Contract Negotiation", desc: "Our legal and sports management experts ensure every contract protects your interests, maximizes earnings, and secures your future." },
  { icon: "TrendingUp", title: "Career Planning", desc: "Long-term career roadmaps tailored to your ambitions — from youth football to retirement and beyond." },
  { icon: "Scale", title: "Legal Advisory", desc: "Access to sports law specialists for contract disputes, employment rights, image rights, and regulatory compliance." },
  { icon: "Megaphone", title: "Sponsorship & Brand Deals", desc: "We connect athletes with commercial partners, kit sponsors, and brand endorsement opportunities that align with their profile." },
  { icon: "Shield", title: "Welfare & Wellbeing", desc: "We support the whole athlete — mental health resources, lifestyle management, and family advisory services." },
  { icon: "Handshake", title: "Club Liaison", desc: "We maintain direct relationships with clubs and sporting directors to represent your interests during transfer windows and renewals." },
];

const DEFAULT_BULLETS = [
  "Licensed agents and sports legal consultants handling all contracts",
  "Tailored commercial endorsement and personal branding strategies",
  "Dedicated athlete support team available 24/7",
  "Proactive career pathway planning across international leagues",
  "Complete financial, legal, and post-career advisory services",
];

export default function PlayerRepresentation() {
  const { getMeta, getText } = useCms();
  const meta = getMeta<{
    heading?: string;
    highlight?: string;
    subtext?: string;
    heroImage?: string;
    overviewHeading?: string;
    overviewText?: string;
    overviewImage?: string;
    bullets?: string[];
    features?: { icon: string; title: string; desc: string }[];
  }>("service_rep", {});

  const heading = meta.heading ?? "PLAYER";
  const highlight = meta.highlight ?? "REPRESENTATION";
  const subtext = meta.subtext ?? getText("service_rep", "Professional, ethical, and athlete-first management. We handle every aspect of your career so you can focus on performing at your best.");
  const heroImage = meta.heroImage || "https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=1600&auto=format&fit=crop";
  const overviewHeading = meta.overviewHeading ?? "YOUR CAREER, OUR PRIORITY";
  const overviewText = meta.overviewText ?? "Eliada Sports Agency offers full-service representation for professional and semi-professional athletes. Our management team works tirelessly on your behalf — in boardrooms, at training grounds, and behind the scenes — so that your career progresses on your terms.\n\nWe believe in transparent, honest representation. Every decision is communicated clearly, and every deal is structured to benefit the athlete first.";
  const overviewImage = meta.overviewImage || "https://images.unsplash.com/photo-1560272564-c83b66b1ad12?w=900&auto=format&fit=crop";
  const bullets = (Array.isArray(meta.bullets) && meta.bullets.length > 0) ? meta.bullets : DEFAULT_BULLETS;
  const features = (Array.isArray(meta.features) && meta.features.length > 0) ? meta.features : DEFAULT_FEATURES;

  return (
    <>
      <SeoHead
        title={`${heading} ${highlight}`}
        description={subtext}
        path="/services/player-representation"
      />
      <PageTransition className="pt-24 pb-0">
        {/* Hero */}
        <div className="relative min-h-[55vh] flex items-center overflow-hidden">
          <img src={heroImage} alt={`${heading} ${highlight}`} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/40 bg-primary/10 text-primary mb-6">
              <Handshake size={14} />
              <span className="text-xs font-display tracking-widest uppercase font-semibold">Our Services</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-display font-bold text-white mb-6 max-w-2xl leading-tight">
              {heading} <span className="text-primary">{highlight}</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-xl mb-8 leading-relaxed">
              {subtext}
            </p>
            <Link href="/join" onClick={() => {}}>
              <Button size="lg" className="gap-2">Get Represented <ArrowRight size={18} /></Button>
            </Link>
          </div>
        </div>

        {/* Overview */}
        <section className="py-24 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
                  {overviewHeading}
                </h2>
                <div className="space-y-4 mb-8">
                  {overviewText.split("\n\n").map((para, i) => (
                    <p key={i} className="text-gray-600 text-lg leading-relaxed">
                      {para}
                    </p>
                  ))}
                </div>
                <ul className="space-y-3">
                  {bullets.map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-gray-700">
                      <CheckCircle className="text-primary shrink-0 w-5 h-5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative">
                <img src={overviewImage} alt="Player Representation" className="rounded-2xl shadow-2xl w-full object-cover" />
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-24 bg-secondary">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-display font-bold text-white mb-4">
                HOW WE <span className="text-primary">MANAGE</span>
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Full-service representation designed around the modern athlete's needs.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((f, i) => {
                const IconComponent = ICON_MAP[f.icon] || Handshake;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    className="bg-background p-8 rounded-2xl border border-white/5 hover:border-primary/40 transition-colors"
                  >
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-5">
                      <IconComponent className="text-primary w-6 h-6" />
                    </div>
                    <h3 className="font-display font-bold text-white text-lg mb-3">{f.title}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{f.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="w-full bg-gradient-to-r from-yellow-600 via-yellow-500 to-yellow-600 py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-4">READY FOR WORLD-CLASS MANAGEMENT?</h2>
            <p className="text-xl text-gray-800 mb-10 font-medium">Connect with an agent today and find out how Eliada Sports Agency can represent your interests.</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link href="/join" onClick={() => {}}><Button size="lg" className="bg-gray-900 text-white hover:bg-black">Get Represented</Button></Link>
              <Link href="/contact" onClick={() => {}}><Button size="lg" variant="outline" className="border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white bg-transparent">Contact Us</Button></Link>
            </div>
          </div>
        </section>
      </PageTransition>
    </>
  );
}
